import { Phase, PhaseInput, StoryLevelData } from "@/app/lib/levels/story-level-data-types";
import WordModule from "@/app/lib/game-state/word-module";
import * as Util from "@/app/lib/system/util";
import { error } from "console";

const ingMarker: string = '#';
const altMarker: string = '@';

export function parseBuilderText(
    text: string,
    levelID?: string // allows us to force an ID, useful internally
) : {
    levelData: StoryLevelData | null,
    errorMessages: string[]
} {

    // we will return these unless we have none
    const errorMessages: string[] = [];

    text = text.trim();
    // if box is empty, return nothing
    if (text === '') {
        return {
            levelData: null,
            errorMessages
        }
    }
    let puzzleTitle: string = 'my puzzle';
    if (!levelID) {
        levelID = puzzleTitle;
    }

    // Check for a title, it must be on the top and start with quotes
    if (text[0] === '"') {
        // Regex for titles, we'll need this shortly
        const titleRE: RegExp = /^"[\x20-\x7E]*"$/; // allows all printed ASCII chars in between quotes
        // find the first line break
        const i = text.indexOf('\n');
        // 0 shouldn't happen (we trimmed), but a negative value means no line breaks
        // Often that means the title is the ONLY thing here
        if (i < 1) {
            if (!titleRE.test(text)) {
                errorMessages.push('Titles must contain only ASCII characters, between two quotes.')
            }
            errorMessages.push('No sections of the puzzle are defined.')
            return {
                levelData: null,
                errorMessages
            };
        }
        // get the first line
        const firstLine: string = text.slice(0, i);
        // abandon it in the original text
        text = text.slice(i).trim();
        // regex test again
        if (!titleRE.test(firstLine)) {
            errorMessages.push('Titles must contain only ASCII characters, between two quotes.')
        }
        // remove the quotes
        puzzleTitle = firstLine.slice(1, -1).toLowerCase(); // removes first/last char
    }

    // If we're empty here we only have a title
    if (text === '') {
        errorMessages.push('No sections of the puzzle are defined');
        return {
            levelData: null,
            errorMessages
        }
    }

    // REGEXP TESTS:
    // Test 1: Checks that only allowed characters are present
    // Allowed: A-Z, a-z, comma, line breaks (\r\n), #, @
    const allowedCharsRE: RegExp = /^[A-Za-z#, @\r\n]*$/;

    // Test 2: Checks for exactly one # character
    const exactlyOneHashRE: RegExp = /^[^#]*#[^#]*$/;

    // Test 3: Checks for at most one @ symbol (0 or 1 allowed)
    const atMostOneAtRE: RegExp = /^(?![\s\S]*@[\s\S]*@)[\s\S]*$/;

    // first we're gonna split the text into "phase texts"
    const phaseTexts: string[] = text
        .split(/\r?\n\s*\r?\n/) // regex that splits around any number of empty lines

    const phaseInputs: PhaseInput[] = [];

    // iterate through the phaseTexts
    for (const [i, phaseText] of phaseTexts.entries()) {
        let words: string[] = [];
        let ingredients: string = '';
        let alternateSolutions: string[] = [];

        if (!allowedCharsRE.test(phaseText)) {
            errorMessages.push(`Only the following characters are allowed in a block: letters, commas, #, and @.`)
        }
        if (!exactlyOneHashRE.test(phaseText)) {
            errorMessages.push(`There must be one and only one # in each block.`)
        }
        
        if (!atMostOneAtRE.test(phaseText)) {
            errorMessages.push(`No more than one @ is allowed in a block.`)
        }

        // if at this point we have error messages, return them
        // we want the puzzle at least moderately well-formed to proceed
        if (errorMessages.length > 0) {
            return {
                levelData: null,
                errorMessages
            };
        }

        // split the texts by their line breaks
        const lines: string[] = phaseText.split(/\r?\n/)

        // scan all characters
        for (const line of lines) {
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '#'
                    && i !== 0
                ) {
                    errorMessages.push('# must be at the start of a line.')
                }
                if (char === '@'
                    && i !== 0
                ) {
                    errorMessages.push('@ must be at the start of a line.')
                }
            }
        }
        // another chance at return
        if (errorMessages.length > 0) {
            return {
                levelData: null,
                errorMessages
            };
        }

        // Now we parse:
        for (const line of lines) {

            // if a line starts with the ingredients marker
            if (line[0] === ingMarker) {
                // parse the line and see if it returns null
                const ingParse = parseIngredientsLine(line);
                if (ingParse.parsed) {
                    ingredients = ingParse.parsed;
                } else if (ingParse.errorMessage) {
                    errorMessages.push(ingParse.errorMessage);
                }
            }

            // if a line starts with alt solutions marker
            else if (line[0] === altMarker) {
                // parse the alt line
                const altParse = parseAltSolutionsLine(line);
                if (altParse.parsed) {
                    alternateSolutions = altParse.parsed;
                } else if (altParse.errorMessage) {
                    errorMessages.push(altParse.errorMessage);
                }
            }

            // otherwise it's a word
            else {
                // parse the word
                const wordParse = parseWordLine(line);
                if (wordParse.parsed) {
                    words.push(wordParse.parsed);
                } else if (wordParse.errorMessage) {
                    errorMessages.push(wordParse.errorMessage);
                }
            }
        }
        /*
        // safety check, because of how we trim lines, it's possible to have ingredients with no words
        if (words.length < 1) {
            errorMessages.push('Ingredients were defined without any words in the block.')
        }
        */

        // Error checkpoint.
        if (errorMessages.length > 0) {
            return {
                levelData: null,
                errorMessages
            };
        }

        // outside of line loop, set our phase
        phaseInputs.push(new PhaseInput(
            words,
            ingredients,
            alternateSolutions
        ));
    }
    
    // outside of phases loop, check that we actually built one properly
    if (phaseInputs.length < 1) {
        errorMessages.push('No sections are properly built for this puzzle.');
        return {
            levelData: null,
            errorMessages
        };
    }

    return {
        levelData: new StoryLevelData(
            puzzleTitle,
            levelID,
            phaseInputs
        ),
        errorMessages
    }
}

function parseWordLine(text: string) : {
    parsed: string | null,
    errorMessage: string | null
} {
    text = text.trim();
    for (const char of text) {
        if (!Util.isAlphaChar(char)) {
            return {
                parsed: null,
                errorMessage: 'Words must be letters only.'
            }
        }
    }
    return {
        parsed: text,
        errorMessage: null
    }
}

function parseIngredientsLine(text: string) : {
    parsed: string | null,
    errorMessage: string | null
} {
    text = text.trim();
    text = text.slice(1); // removes the #
    for (const char of text) {
        if (!Util.isAlphaChar(char)) {
            return {
                parsed: null,
                errorMessage: 'Ingredients must be letters.'
            }
        }
    }
    return {
        parsed: text.toLowerCase(),
        errorMessage: null
    };
}

function parseAltSolutionsLine(text: string) : {
    parsed: string[] | null,
    errorMessage: string | null
} {
    text = text.trim();
    text = text.slice(1); // removes the @
    const altSolutionWords = text.split(',');
    const altSolutions: string[] = [];
    for (const altSolutionWord of altSolutionWords) {
        const word = altSolutionWord.trim();
        for (const char of word) {
            if (!Util.isAlphaChar(char)) {
                return {
                    parsed: null,
                    errorMessage: 'Alternate solutions must be composed of letters only.'
                }
            }
        }
        altSolutions.push(word);
    }
    return {
        parsed: altSolutions,
        errorMessage: null
    };
}

// We separate these warnings, allowing the puzzle to render (but not launch) while they're present
export function validateBuilderPuzzle(levelData: StoryLevelData) : {
    success: boolean,
    errorMessage: string
} {
    let errorMessage: string = '';
    let success: boolean = true;

    // we need to make sure that each word can be solved w/ ingredients
    for (const phase of levelData.phases) {
        // if there are no words
        if (phase.wordIndices.length === 0) {
            success = false;
            errorMessage += `● Section ${phase.index + 1} doesn't have any words!\n`
        }
        // if thera are no ingredients just say that!
        if (phase.ingredients.length === 0) {
            success = false;
            errorMessage += `● Section ${phase.index + 1} doesn't have any ingredients!\n`
        }
        for (const wordIndex of phase.wordIndices) {
            const wordModule = new WordModule(levelData.words[wordIndex]);
            if (!wordModule.isSolvableWithIngredients(phase.ingredients)) {
                success = false;
                let missingIngredients = wordModule.getMissingIngredients(phase.ingredients);
                missingIngredients = missingIngredients.map(i => i.toUpperCase());
                errorMessage += `● In section ${phase.index + 1}, "${wordModule.stringifyComplete()}" is missing ingredients: ${missingIngredients}.\n`
            }
        }
    }

    // check that we have a valid path to solve all words
    const validator = new PuzzleValidator(levelData);
    const errorPaths = validator.findErrorPaths();
    if (errorPaths.length > 0) {
        success = false;
        for (const errorPath of errorPaths) {
            // the last word in the path is unsolvable
            const unsolvableWord = errorPath.at(-1)?.word;
            if (!unsolvableWord)
                continue;
            errorMessage += `\n● The following solution order makes "${unsolvableWord} unsolvable:\n`
            for (const [i, pair] of errorPath.entries()) {
                if (pair.solution) {
                    errorMessage += `   ${i + 1}) "${pair.word}" solved with "${pair.solution}"\n`
                }
            }
            errorMessage += `Consider adding an alternate solution.\n`
        }
    }

    errorMessage = errorMessage.trim();

    return ({
        success,
        errorMessage
    })
}

type Path = Array<{
    word: string,
    solution: string
}>

type SolutionGroup = {
    words: WordModule[],
    solution: string
}

class PuzzleValidator {

    levelData: StoryLevelData;
    allWords: WordModule[] = [];
    allSolutions: string[] = [];
    solutionsByPhaseIndex: Map<
        number,
        string[]
    > = new Map();
    phaseByWordIndex: Map<
        number,
        Phase
    > = new Map();
    solutionMatchesByWordIndex: Map<
        number,
        string[]
    > = new Map();

    constructor(levelData: StoryLevelData) {
        this.levelData = levelData;

        // populate the solutions map
        for (const phase of levelData.phases) {
            // iterate through the wordIndices of phase to get the  words
            const words: WordModule[] = [];
            for (const wordIndex of phase.wordIndices) {
                const wordModule = new WordModule(levelData.words[wordIndex]);
                // important to load the index into the module
                wordModule.index = wordIndex;
                // match the phase to the word index in the map
                this.phaseByWordIndex.set(wordIndex, phase);
                words.push(wordModule);
                this.allWords.push(wordModule);
            }
            // now that we have the words we can use that to populate solutions
            const phaseSolutions: string[] = [];
            for (const wordModule of words) {
                if (!phaseSolutions.includes(wordModule.puzzleWord)) {
                    phaseSolutions.push(wordModule.puzzleWord);
                }
                if (!this.allSolutions.includes(wordModule.puzzleWord)) {
                    this.allSolutions.push(wordModule.puzzleWord);
                }
            }
            for (const altSolution of phase.altSolutions) {
                if (!phaseSolutions.includes(altSolution)) {
                    phaseSolutions.push(altSolution);
                    this.allSolutions.push(altSolution);
                }
                if (!this.allSolutions.includes(altSolution)) {
                    this.allSolutions.push(altSolution);
                }
            }
            // load solutions into the map
            this.solutionsByPhaseIndex.set(phase.index, phaseSolutions);
        }
        // with the other maps built, cache solution matches for each word
        for (const word of this.allWords) {
            const solutionMatches = this.getSolutionMatches(word);
            this.solutionMatchesByWordIndex.set(word.index, solutionMatches);
        }
    }
   
    findErrorPaths(options: {
        ignoreUnsolvableFromIngredients?: boolean,
        returnOnePathPerUnsolvable?: boolean
    } = {}): Path[]  {
        // destructure to default true values:
        const {
            ignoreUnsolvableFromIngredients = true,
            returnOnePathPerUnsolvable = true
        } = options;

        const errorPathReturns: Path[] = []
        const dangerGroups = this.findDangerGroups();

        // iterate through each danger group
        for (const dangerGroup of dangerGroups) {
            // we are going to recursively check each group
            // but we know the initial path
            // it is the solution in each group, applied to each word
            // so we do that iteratively
            for (const [i, word] of dangerGroup.words.entries()) {
                const initialErrorPaths: Path[] = [];
                const initialPath: Path = [{
                    word: word.stringifyComplete(),
                    solution: dangerGroup.solution
                }];
                const remainingWords =[
                    ...dangerGroup.words.slice(0, i),
                    ...dangerGroup.words.slice(i + 1)
                ]
                const errorPaths = this.recursivePathSearch(
                    remainingWords,
                    initialPath,
                    initialErrorPaths,
                    ignoreUnsolvableFromIngredients
                )
                // if we limit the returns to one path per unsolvable
                // ... then we have to check them against paths already found
                if (returnOnePathPerUnsolvable) {
                    // build an array of already found unsolvable words
                    const alreadyFoundUnsolvables: string[] = [];
                    for (const errorPathReturn of errorPathReturns) {
                        const lastPathStop = errorPathReturn.at(-1);
                        if (
                            lastPathStop
                            && lastPathStop.solution === '' // safety check that it's unsolvable
                        ) {
                            alreadyFoundUnsolvables.push(lastPathStop.word)
                        }
                    }
                    for (const errorPath of errorPaths) {
                        const lastPathStop = errorPath.at(-1);
                        if (
                            lastPathStop
                            && lastPathStop.solution === '' // safety check that it's unsolvable
                            && !alreadyFoundUnsolvables.includes(lastPathStop.word)
                        ) {
                            errorPathReturns.push(errorPath);
                            alreadyFoundUnsolvables.push(lastPathStop.word);
                        }
                    }
                } else {
                    errorPathReturns.push(...errorPaths);
                }
            }
        }
        return errorPathReturns;
    }

    // Danger groups are words that all share a solution
    findDangerGroups(): SolutionGroup[] {
        const dangerGroups: SolutionGroup[] = [];

        // find solutions that apply to more than one word
        for (const solution of this.allSolutions) {
            const solutionGroup: SolutionGroup = {
                words: [],
                solution: solution
            }
            // check each word against this solution
            for (const word of this.allWords) {
                const solutionMatches: string[] | undefined
                    = this.solutionMatchesByWordIndex.get(word.index);
                if (!solutionMatches) {
                    console.log('Puzzle Validator error FDG-00')
                    continue;
                }
                // if this solution is one of the matches for our word, we put it in the group
                if (solutionMatches.includes(solution)) {
                    solutionGroup.words.push(word);
                }
            }
            // if the group has more than one word, it's a danger group
            if (solutionGroup.words.length > 1) {
                dangerGroups.push(solutionGroup);
            }
        }
        return dangerGroups;
    }

    getSolutionMatches(word : WordModule) : string[] {
        const matches: string[] = [];
        const phase: Phase | undefined = this.phaseByWordIndex.get(word.index);
        if (!phase) {
            console.log('Puzzle Validator error GSM-00')
            return matches;
        }
        const phaseSolutions: string[] | undefined = this.solutionsByPhaseIndex.get(phase.index);
        if (!phaseSolutions) {
            console.log('Puzzle Validator error GSM-01')
            return matches;
        }
        for (const solution of phaseSolutions) {
            if (word.validate(solution, phase.ingredients)) {
                matches.push(solution);
            }
        }
        return matches;
    }

    recursivePathSearch(
        words: WordModule[],
        path: Path,
        errorPaths: Path[],
        ignoreUnsolvableFromIngredients: boolean,
    ): Path[] {
        // the short circuit
        if (words.length === 0) {
            // we don't acutally access the return value from the short circuit (see below)
            return errorPaths;
        }

        // path has a history of all solutions used. we'll need shortly
        const usedSolutions: string[] = path.map(p => p.solution);

        // iterate through the words 
        for (const [i, word] of words.entries()) {
            const phase: Phase | undefined = this.phaseByWordIndex.get(word.index);
            if (!phase) {
                Util.devLog("Puzzle Validator Error RPS-00")
                continue;
            }
            // a word is naively unsolvable if we don't have the ingredients
            // we don't care about that here, usually
            if (
                ignoreUnsolvableFromIngredients
                && !word.isSolvableWithIngredients(phase.ingredients)
            ) {
                continue;
            }
            // get all solution matches, potential matches are specific to each phase
            const phaseSolutions: string[] | undefined = this.solutionsByPhaseIndex.get(phase.index)
            if (!phaseSolutions) {
                Util.devLog("Puzzle Validator ERror RPS-01")
                continue;
            }
            const solutionMatches = this.solutionMatchesByWordIndex.get(word.index);
            if (!solutionMatches) {
                Util.devLog("Puzzle Validator Error RPS-02")
                continue;
            }
            // filter out anything already used
            const availableSolutions = solutionMatches
                .filter(s => !usedSolutions.includes(s));
        
            // if none available, we're on a bad path
            if (availableSolutions.length === 0) {
                // build a unique var for this path
                const thisPath: Path = [
                    ...path,
                    {
                        word: word.stringifyComplete(),
                        solution: '' // empty solution for unsolvable path end
                    }
                ]
                // add it to error paths to keep across all recursive calls
                errorPaths.push(thisPath);
                continue;
            }
            // remove the word for recursive call
            // make a new array by cutting around the word
            const remainingWords =[
                ...words.slice(0, i),
                ...words.slice(i + 1)
            ]
            // now iterate through each available solution
            for (const availableSolution of availableSolutions) {
                // add it to this path
                const thisPath = [
                    ...path,
                    {
                        word: word.stringifyComplete(),
                        solution: availableSolution
                    }
                ];
                // we don't need to return the recursive call
                // progress is saved across calls in errorPaths via .push()
                // we simply make the call:
                this.recursivePathSearch(
                    remainingWords,
                    thisPath,
                    errorPaths,
                    ignoreUnsolvableFromIngredients
                );
            }
        }
        // this is the return statement that will pass the value back to the root caller
        // (unless we start with an empty words list)
        return errorPaths;
    }
}